import { TypedDocumentNode, gql } from '@apollo/client';
import {
  addHours,
  eachDayOfInterval,
  // eslint-disable-next-line no-restricted-imports
  format,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
} from 'date-fns';
import { useState } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  defineMessages,
} from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { EmptyState } from '@sorare/core/src/components/EmptyState';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import useInfiniteScroll from '@sorare/core/src/hooks/useInfiniteScroll';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { getSafePreviousLocation } from '@sorare/core/src/lib/navigation/getSafePreviousLocation';

import { WithFixtureHeaderLayout } from 'layouts/WithFixtureHeaderLayout';

import { MatchCenterGame } from './MatchCenterGame';
import {
  MatchCenterPage_so5Fixture,
  MatchCenterPaginatedQuery,
  MatchCenterPaginatedQueryVariables,
} from './__generated__/index.graphql';
import emptyIcon from './assets/empty_icon.svg';

const messages = defineMessages({
  noneOfYourPlayersPlaying: {
    id: 'MatchCenterPage.noneOfYourPlayersPlaying',
    defaultMessage: 'None of your players are playing a match on this day.',
  },
  noneOfYourPlayersPlayingBefore: {
    id: 'MatchCenterPage.noneOfYourPlayersPlayingBefore',
    defaultMessage:
      'None of your players are playing a match on this day before {time}.',
  },
  noneOfYourPlayersPlayingAfter: {
    id: 'MatchCenterPage.noneOfYourPlayersPlayingAfter',
    defaultMessage:
      'None of your players are playing a match on this day after {time}.',
  },
});

const MATCH_CENTER_PAGINATED_QUERY = gql`
  query MatchCenterPaginatedQuery(
    $slug: String!
    $fromDate: ISO8601DateTime!
    $toDate: ISO8601DateTime!
    $after: String
  ) {
    so5 {
      so5Fixture(slug: $slug) {
        slug
        gamesWithMyPlayerGameScoresPreview(
          first: 20
          after: $after
          fromDate: $fromDate
          toDate: $toDate
        ) {
          nodes {
            ...MatchCenterGame_gameWithPlayerGameScoresPreview
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${MatchCenterGame.fragments.gameWithPlayerGameScoresPreview}
` as TypedDocumentNode<
  MatchCenterPaginatedQuery,
  MatchCenterPaginatedQueryVariables
>;

const Content = styled.div`
  padding: var(--double-unit) 0;
`;

type Props = {
  so5Fixture: MatchCenterPage_so5Fixture;
  onClickPlayerGameScore?: (playerGameScoreId: string) => void;
  onClickGame?: (gameId: string, myPlayerSlugs?: string[]) => void;
};

export const MatchCenterPage = ({
  so5Fixture,
  onClickPlayerGameScore,
  onClickGame,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();
  const { generateSportContextPath } = useSportContext();

  const [selectedDay, setSelectedDay] = useState<string>();

  const safePreviousLocation = getSafePreviousLocation(
    generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
      params: {
        eventType: so5Fixture.type.toLowerCase(),
        fixture: so5Fixture.slug,
      },
    }),
    -1
  );

  const fixtureDays = eachDayOfInterval({
    start: so5Fixture.startDate,
    end: so5Fixture.endDate,
  }).map(date => format(date, 'yyyy-MM-dd'));

  const today = new Date();
  if (!selectedDay && fixtureDays.length) {
    if (
      isAfter(today, so5Fixture.startDate) &&
      isBefore(today, so5Fixture.endDate)
    ) {
      setSelectedDay(format(today, 'yyyy-MM-dd'));
    } else {
      setSelectedDay(fixtureDays[0]);
    }
  }

  const selectedDayStart = selectedDay
    ? startOfDay(parseISO(selectedDay))
    : null;
  const selectedDayEnd = selectedDayStart
    ? addHours(selectedDayStart, 24)
    : null;

  const { data, loading, loadMore } = usePaginatedQuery(
    MATCH_CENTER_PAGINATED_QUERY,
    {
      variables: {
        slug: so5Fixture.slug,
        fromDate: selectedDayStart!,
        toDate: selectedDayEnd!,
      },
      connection: 'GameWithPlayerGameScoresPreviewConnection',
      skip: !selectedDay,
    }
  );

  const { nodes = [], pageInfo } =
    data?.so5.so5Fixture?.gamesWithMyPlayerGameScoresPreview || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, { after: pageInfo?.endCursor });
    },
    !!pageInfo?.hasNextPage,
    loading
  );

  let emptyMessage = messages.noneOfYourPlayersPlaying;
  if (selectedDay === fixtureDays[0]) {
    emptyMessage = messages.noneOfYourPlayersPlayingAfter;
  }
  if (selectedDay === fixtureDays[fixtureDays.length - 1]) {
    emptyMessage = messages.noneOfYourPlayersPlayingBefore;
  }

  return (
    <WithFixtureHeaderLayout
      so5Fixture={so5Fixture}
      title={
        <FormattedMessage
          {...(so5Fixture.sport === Sport.FOOTBALL
            ? fantasy.matchCenter
            : fantasy.gameCenter)}
        />
      }
      backTo={safePreviousLocation}
    >
      <Content>
        <Vertical gap={3}>
          <TabBar
            value={selectedDay}
            onValueChange={setSelectedDay}
            fullWidth
            variant="flat"
          >
            {fixtureDays.map(day => (
              <Tab key={day} value={day}>
                <FormattedDate
                  value={day}
                  weekday="short"
                  day="2-digit"
                  month="short"
                  timeZone="UTC"
                />
              </Tab>
            ))}
          </TabBar>
          <Vertical gap={isLaptopAndAbove ? 0 : 2}>
            {!loading && !nodes?.length ? (
              <EmptyState
                title={
                  <FormattedMessage
                    id="MatchCenterPage.noGames"
                    defaultMessage="No Games"
                  />
                }
                message={
                  <FormattedMessage
                    {...emptyMessage}
                    values={{
                      time: (
                        <FormattedTime
                          value={
                            selectedDay === fixtureDays[0]
                              ? so5Fixture.startDate
                              : so5Fixture.endDate
                          }
                        />
                      ),
                    }}
                  />
                }
                imageUrl={emptyIcon}
              />
            ) : (
              <>
                {nodes?.map(gameWithPgsPreview => (
                  <MatchCenterGame
                    key={gameWithPgsPreview.game.id}
                    gameWithPlayerGameScoresPreview={gameWithPgsPreview}
                    onClickGame={onClickGame}
                    onClickPlayerGameScore={onClickPlayerGameScore}
                  />
                ))}
                <InfiniteScrollLoader />
              </>
            )}
          </Vertical>
        </Vertical>
      </Content>
    </WithFixtureHeaderLayout>
  );
};

MatchCenterPage.fragments = {
  so5Fixture: gql`
    fragment MatchCenterPage_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
      type
      sport
      ...WithFixtureHeaderLayout_so5Fixture
    }
    ${WithFixtureHeaderLayout.fragments.so5Fixture}
  ` as TypedDocumentNode<MatchCenterPage_so5Fixture>,
};
